import type {CSSProperties} from 'react';
import {radius} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeTabMenuLook = 'tab-menu-row' | 'tab-menu-row-first' | 'desk-refresh' | 'desk-refresh-compact' | 'desk-refresh-compact-warn';

const TAB_MENU_LOOKS = new Set<string>(['tab-menu-row', 'tab-menu-row-first', 'desk-refresh', 'desk-refresh-compact', 'desk-refresh-compact-warn']);
export const isWedgeTabMenuLook = (look: string): look is WedgeTabMenuLook => TAB_MENU_LOOKS.has(look);

/* Released tab action menu (the long-press peek over the dock) and desk refresh button. The menu row is a full-width flex row with a 12px gap
   and 14px by 16px of padding, a bare fill, no border but a 0.5px separator on top (the first row has none), the Wedge font and the primary
   ink. It is a 58px row at least (a 30px chip and 28px of padding) and the rows sit edge to edge, so it draws no transparent target. The desk
   refresh button is a bare 36px round outline in the secondary ink; its compact form is a bare 44px box with a 9px negative margin (a layout
   contract of the hero card header: the 26px ring inside it is the visible control) in the secondary ink, or the amber ink when the source
   is stale. The compact box is already 44px, so it draws no target either. Neither refresh look sets a fill: the caller keeps the shipped
   `.rv-rail-chip` class, whose rule paints the bare fill and the hover tint, and an inline value would beat it. Each reads the exact legacy
   roles, and the declaration order is the order of the source. The caller owns the glyphs, the labels and the handlers. */
const ROW_GAP = 12, ROW_PADDING = '14px 16px', SEPARATOR_WIDTH = '0.5px', COMPACT_BOX = 44, COMPACT_MARGIN = -9, FULL_BOX = 36;

export function wedgeTabMenuStyle(look: WedgeTabMenuLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  const tap = {WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation'} as const;
  if (look === 'desk-refresh') return {
    width: FULL_BOX, height: FULL_BOX, borderRadius: radius.pill, border: `1px solid ${r('cardBorder')}`, color: r('labelSec'),
    cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...tap,
  };
  if (look === 'desk-refresh-compact' || look === 'desk-refresh-compact-warn') return {
    width: COMPACT_BOX, height: COMPACT_BOX, margin: COMPACT_MARGIN, padding: 0, border: 0, background: 'transparent',
    color: r(look === 'desk-refresh-compact-warn' ? 'amber' : 'labelSec'), cursor: disabled ? 'default' : 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...tap,
  };
  return {
    width: '100%', background: 'none', border: 0, display: 'flex', alignItems: 'center', gap: ROW_GAP, padding: ROW_PADDING,
    borderTop: look === 'tab-menu-row-first' ? 'none' : `${SEPARATOR_WIDTH} solid ${r('separator')}`, cursor: 'pointer', fontFamily: r('font'), color: r('label'),
  };
}
